import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { getUnitAttachments, setUnitAttachment } from '@portfolio/actions/portfolio.actions';
import { UnitDetailsAbstract } from '@portfolio/components/unit-details/abstract/unit-details.abstract';
import { selectUnitAttachments } from '@portfolio/selectors/portfolio.selectors';
import { imageVideoFileExtensions } from '@shared/constants/file-extensions.const';
import { UnitAttachmentType } from '@shared/enums/attachment-type';
import { AttachmentItem } from '@shared/interfaces/attachment-item';
import { RestGenericTypedAttachment } from '@shared/interfaces/attachment.interface';
import { UpdateTypedAttachmentToEntityModel } from '@shared/interfaces/turnover.interface';
import { Upload } from '@shared/interfaces/upload';
import { ModalsService } from '@ui-components/modals/modals.service';

export enum UnitDetailsPicturesSectionComponentForm {
  PHOTO = 'photo',
  FLOOR_PLAN = 'floorPlan',
}

@UntilDestroy()
@Component({
  selector: 'app-unit-details-pictures-section',
  templateUrl: './unit-details-pictures-section.component.html',
  styleUrls: ['./unit-details-pictures-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitDetailsPicturesSectionComponent extends UnitDetailsAbstract implements OnInit, OnDestroy {
  fileUploadInProgress = false;
  form: UntypedFormGroup;
  unitPhotos: RestGenericTypedAttachment[] = [];
  unitFloorPlan: RestGenericTypedAttachment[] = [];
  unitPhotosBSubject: BehaviorSubject<RestGenericTypedAttachment[]> = new BehaviorSubject<RestGenericTypedAttachment[]>(
    []
  );
  unitFloorPlanBSubject: BehaviorSubject<RestGenericTypedAttachment[]> = new BehaviorSubject<
    RestGenericTypedAttachment[]
  >([]);
  displayedUnitPhotos: RestGenericTypedAttachment[] = [];
  displayedUnitFloorPlan: RestGenericTypedAttachment[] = [];
  UnitAttachmentType = UnitAttachmentType;
  protected readonly acceptedFormats = imageVideoFileExtensions;

  constructor(
    protected cdr: ChangeDetectorRef,
    private store: Store<{}>,
    private formBuilder: UntypedFormBuilder,
    private modalsService: ModalsService
  ) {
    super(cdr);
  }

  ngOnInit(): void {
    this.initForm();

    this.store.dispatch(getUnitAttachments({ unitId: this.unitId }));

    this.store
      .select(selectUnitAttachments)
      .pipe(
        untilDestroyed(this),
        filter((unitAttachments: RestGenericTypedAttachment[]) => !!unitAttachments),
        tap((unitAttachments: RestGenericTypedAttachment[]) => {
          this.prepareUnitAttachments(unitAttachments);
          this.cdr.detectChanges();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {}

  fileUploaded(item: AttachmentItem, unitAttachmentType: UnitAttachmentType) {
    if (item?.upload) {
      this.setAttachment(item.upload, unitAttachmentType);
    }
  }

  showUnitPictures() {
    this.modalsService.openGalleryModal({
      attachmentType: UnitAttachmentType.Photo,
      attachmentsBSubject: this.unitPhotosBSubject,
      readOnly: this.readOnly,
      unitId: this.unitId,
    });
  }

  showUnitFloorPlans() {
    this.modalsService.openGalleryModal({
      attachmentType: UnitAttachmentType.FloorPlan,
      attachmentsBSubject: this.unitFloorPlanBSubject,
      readOnly: this.readOnly,
      unitId: this.unitId,
    });
  }

  private setAttachment(upload: Upload, attachmentType: UnitAttachmentType) {
    const attachmentToEntity: UpdateTypedAttachmentToEntityModel = {
      externalUrl: '',
      unitId: this.unitId,
      propertyId: 0,
      portfolioId: 0,
      turnoverIds: [],
      applianceId: 0,
      bidId: 0,
      ticketId: 0,
      attachmentType,
      fileUploadId: upload?.id,
      title: upload?.originalFileName,
      description: '',
    };
    this.store.dispatch(setUnitAttachment({ attachmentToEntity }));
  }

  private initForm() {
    this.form = this.formBuilder.group({
      [UnitDetailsPicturesSectionComponentForm.PHOTO]: [null, [Validators.required]],
      [UnitDetailsPicturesSectionComponentForm.FLOOR_PLAN]: [null, [Validators.required]],
    });
  }

  private prepareUnitAttachments(unitAttachments: RestGenericTypedAttachment[]) {
    this.unitPhotos = [];
    this.unitFloorPlan = [];

    unitAttachments?.forEach(item => {
      switch (item.targetAttachmentType as UnitAttachmentType) {
        case UnitAttachmentType.Photo:
          {
            this.unitPhotos.push(item);
          }
          break;
        case UnitAttachmentType.FloorPlan:
          {
            this.unitFloorPlan.push(item);
          }
          break;
      }
    });

    this.unitPhotosBSubject.next(this.unitPhotos);
    this.unitFloorPlanBSubject.next(this.unitFloorPlan);

    this.displayedUnitPhotos = this.unitPhotos.slice(0, 2);
    this.displayedUnitFloorPlan = this.unitFloorPlan.slice(0, 2);
  }
}
